import { useOktaAuth } from '@okta/okta-react';
import {
  bootEngagement,
  initAnalytics,
  initEngagement,
  setAmplitudeEventEnrichmentProperties,
  setEventProperties,
  setSharedProperties,
  setUserProperties,
} from '@wmgone/analytics';
import { type PropsWithChildren, useContext, useEffect, useState } from 'react';

import { env } from '~/lib/env';
import { logger } from '~/lib/logger';
import { useUser } from '~/modules/auth/hooks/useUser';
import { BrandContext } from '~/modules/branding';
import { openFeatureFlag } from '~/modules/experiments/lib/OpenFeatureFlagConnector';
import { checkUserForOptOut } from '~/modules/monitoring/analytics/utils/checkUserForOptOut';

import { initContainerAnalytics } from './useAnalytics';
import { getAmplitudeEventEnrichmentProperties } from './utils/getAmplitudeEventEnrichmentProperties';
import { getDefaultEventProperties } from './utils/getDefaultEventProperties';
import { getDefaultSharedProperties } from './utils/getDefaultSharedProperties';
import { getDefaultUserProperties } from './utils/getDefaultUserProperties';

/**
 * Component used in the Container to initialize the Analytics and  set global
 * analytics user properties.
 */
export function Analytics({ children }: PropsWithChildren) {
  const user = useUser();
  const brand = useContext(BrandContext);
  const { authState } = useOktaAuth();
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // We only initialize the analytics module once we have a fully logged in
    // and authenticated user.
    if (authState?.isAuthenticated) {
      // Initialize the base analytics module.
      initAnalytics(env('VITE_AMPLITUDE_API_KEY', false));

      const isAnalyticsOptedOut = checkUserForOptOut(user);

      // Properties set only on the User.
      setUserProperties(getDefaultUserProperties(brand));

      // Properties tracked only at the Event level for each Event.
      setEventProperties(getDefaultEventProperties(brand));

      // Properties set on the User and also tracked with each Event.
      const sharedProperties = getDefaultSharedProperties(user);

      setSharedProperties(sharedProperties);

      // Properties used to enrich the Amplitude events.
      setAmplitudeEventEnrichmentProperties(
        getAmplitudeEventEnrichmentProperties(brand, user),
      );

      initContainerAnalytics();

      const isEngagementEnabled =
        env('VITE_AMPLITUDE_GUIDES_AND_SURVEYS_ENABLED', false) !== 'false';

      if (isEngagementEnabled && !isAnalyticsOptedOut) {
        try {
          initEngagement(env('VITE_AMPLITUDE_API_KEY', false));
          void bootEngagement({
            userId: String(sharedProperties.user_id ?? sharedProperties.email),
            userProperties: sharedProperties,
          }).catch((error) => {
            logger.error('Failed to boot Amplitude engagement', error);
          });
        } catch (error) {
          logger.error('Failed to initialize Amplitude engagement', error);
        }
      }

      openFeatureFlag.init().then(() => {
        setLoading(false);
      });
    }
  }, [authState?.isAuthenticated, brand, user]);

  if (loading) {
    return null;
  }

  return <>{children}</>;
}

Analytics.displayName = 'Analytics';
